/**
 * Business Content Context
 * Provides business-type-specific content (pipeline stages, templates, scripts, etc.)
 * to all components in the application. Fetches from /api/dashboard/business-content
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Default content fallback (general business type)
const DEFAULT_CONTENT = {
  business_type: 'general',
  business_name: 'Your Business',
  pipeline_stages: ['New Lead', 'Contacted', 'Qualified', 'Proposal Sent', 'Negotiation', 'Closed Won', 'Closed Lost'],
  dashboard: {
    title: 'Welcome to your Business CRM',
    quick_actions: [
      { icon: 'phone', label: 'Call Lead', action: 'call_lead' },
      { icon: 'mail', label: 'Send Email', action: 'send_email' },
      { icon: 'trending-up', label: 'View Pipeline', action: 'view_pipeline' }
    ],
    metrics: { leads: 'Leads in Pipeline', conversions: 'Deals Closed', revenue: 'Revenue This Month' }
  },
  email_templates: [],
  whatsapp_templates: {
    greeting: 'Hi {name}! This is {business_name}. How can we help you today?',
    followup: 'Hi {name}, following up on your enquiry. When can we connect?',
    offer: 'Hi {name}! We have a special offer for you!'
  },
  call_script: {
    greeting: 'Hello {name}! This is {agent_name} from {business_name}. I\'m calling about your recent enquiry.',
    questions: ['How can I help you?', 'What are you looking for?', 'Any specific requirements?', 'What\'s your timeline?', 'Want more information?'],
    closing: 'I\'ll send more details and follow up soon.'
  },
  page_templates: [],
  automation_sequence: []
};

const BusinessContentContext = createContext();

export const useBusinessContent = () => {
  const context = useContext(BusinessContentContext);
  if (!context) {
    throw new Error('useBusinessContent must be used within a BusinessContentProvider');
  }
  return context;
};

export const BusinessContentProvider = ({ children }) => {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  const fetchBusinessContent = useCallback(async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setContent(DEFAULT_CONTENT);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const res = await axios.get(`${BACKEND_URL}/api/dashboard/business-content`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data) {
        setContent({
          ...DEFAULT_CONTENT,
          ...res.data
        });
        setLastFetched(new Date());
      }
    } catch (err) {
      console.error('Failed to fetch business content:', err);
      setError(err.message);
      // Keep default content on error
      setContent(DEFAULT_CONTENT);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount
  useEffect(() => {
    fetchBusinessContent();
  }, [fetchBusinessContent]);

  // Listen for business type changes from settings
  useEffect(() => {
    const handleBusinessTypeChange = () => {
      fetchBusinessContent();
    };

    window.addEventListener('businessTypeChanged', handleBusinessTypeChange);
    return () => {
      window.removeEventListener('businessTypeChanged', handleBusinessTypeChange);
    };
  }, [fetchBusinessContent]);

  // Method to update business type and refresh content
  const updateBusinessType = useCallback(async (newBusinessType) => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    try {
      setLoading(true);
      const res = await axios.post(
        `${BACKEND_URL}/api/dashboard/update-business-type`,
        { business_type: newBusinessType },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data?.content) {
        setContent({
          ...DEFAULT_CONTENT,
          ...res.data.content
        });
        setLastFetched(new Date());
        
        // Dispatch event for other components to know
        window.dispatchEvent(new CustomEvent('businessTypeChanged', {
          detail: { businessType: newBusinessType }
        }));
      }
    } catch (err) {
      console.error('Failed to update business type:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Helper function to get pipeline stages for CRM
  const getPipelineStages = useCallback(() => {
    return content.pipeline_stages || DEFAULT_CONTENT.pipeline_stages;
  }, [content.pipeline_stages]);

  // Helper function to get dashboard content
  const getDashboardContent = useCallback(() => {
    return content.dashboard || DEFAULT_CONTENT.dashboard;
  }, [content.dashboard]);

  // Helper function to get WhatsApp templates
  const getWhatsAppTemplates = useCallback(() => {
    return content.whatsapp_templates || DEFAULT_CONTENT.whatsapp_templates;
  }, [content.whatsapp_templates]);

  // Helper function to get AI call script
  const getCallScript = useCallback(() => {
    return content.call_script || DEFAULT_CONTENT.call_script;
  }, [content.call_script]);

  // Helper function to get email templates
  const getEmailTemplates = useCallback(() => {
    return content.email_templates || DEFAULT_CONTENT.email_templates;
  }, [content.email_templates]);

  // Helper function to get page/funnel templates
  const getPageTemplates = useCallback(() => {
    return content.page_templates || DEFAULT_CONTENT.page_templates;
  }, [content.page_templates]);

  // Helper function to get automation sequence
  const getAutomationSequence = useCallback(() => {
    return content.automation_sequence || DEFAULT_CONTENT.automation_sequence;
  }, [content.automation_sequence]);

  // Get business type display name
  const getBusinessTypeName = useCallback(() => {
    const typeNames = {
      real_estate: 'Real Estate',
      insurance: 'Insurance',
      training: 'Training & Education',
      healthcare: 'Healthcare',
      automobile: 'Automobile',
      restaurant: 'Restaurant',
      ecommerce: 'E-commerce',
      finance: 'Finance & Loans',
      travel: 'Travel & Tourism',
      general: 'General Business',
      other: 'Other'
    };
    return typeNames[content.business_type] || 'General Business';
  }, [content.business_type]);

  // Get metric labels based on business type
  const getMetricLabels = useCallback(() => {
    return content.dashboard?.metrics || DEFAULT_CONTENT.dashboard.metrics;
  }, [content.dashboard]);

  const value = {
    content,
    loading,
    error,
    lastFetched,
    businessType: content.business_type,
    businessName: content.business_name,
    
    // Actions
    refreshContent: fetchBusinessContent,
    updateBusinessType,
    
    // Helpers
    getPipelineStages,
    getDashboardContent,
    getWhatsAppTemplates,
    getCallScript,
    getEmailTemplates,
    getPageTemplates,
    getAutomationSequence,
    getBusinessTypeName,
    getMetricLabels
  };

  return (
    <BusinessContentContext.Provider value={value}>
      {children}
    </BusinessContentContext.Provider>
  );
};

export default BusinessContentContext;
